import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldOff, Loader2, AlertCircle } from "lucide-react";

const GIS_SRC = "https://accounts.google.com/gsi/client";

function ensureGoogleIdentityServicesLoaded() {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.accounts && window.google.accounts.id) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("GIS load failed")), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("GIS load failed"));
    document.head.appendChild(s);
  });
}

export default function Google2FACard() {
  const { user, refreshAuth } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const r = await api.get("/auth/google/config");
      setCfg(r.data);
    } catch (e) { /* keep null */ }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const enable = async () => {
    if (!cfg) return;
    setBusy(true);
    try {
      if (cfg.enabled && cfg.client_id) {
        // LIVE — use Google Identity Services for a real id_token
        await ensureGoogleIdentityServicesLoaded();
        await new Promise((resolve, reject) => {
          window.google.accounts.id.initialize({
            client_id: cfg.client_id,
            callback: async (response) => {
              try {
                const r = await api.post("/auth/google/verify", { id_token: response.credential });
                if (r.data?.token) {
                  localStorage.setItem("acas_token", r.data.token);
                }
                toast.success("Google 2FA enabled");
                refreshAuth && refreshAuth();
                loadConfig();
                resolve();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Verification failed");
                reject(e);
              }
            },
          });
          window.google.accounts.id.prompt((notif) => {
            // If the prompt couldn't display (e.g., user dismissed One-Tap many times),
            // fall back to rendering a button.
            if (notif.isNotDisplayed() || notif.isSkippedMoment()) {
              const node = document.createElement("div");
              node.id = "google-signin-fallback";
              node.style.position = "fixed";
              node.style.top = "50%"; node.style.left = "50%";
              node.style.transform = "translate(-50%, -50%)";
              node.style.zIndex = "9999";
              node.style.background = "white";
              node.style.padding = "16px";
              node.style.borderRadius = "12px";
              node.style.boxShadow = "0 6px 32px rgba(0,0,0,0.18)";
              document.body.appendChild(node);
              window.google.accounts.id.renderButton(node, { theme: "outline", size: "large", type: "standard" });
              setTimeout(() => { node.remove(); }, 60000);
            }
          });
        });
      } else {
        // MOCK \u2014 backend will accept any id_token in dev
        const r = await api.post("/auth/google/verify", { id_token: "mock-token" });
        if (r.data?.token) localStorage.setItem("acas_token", r.data.token);
        toast.success("Google 2FA enabled (MOCK mode \u2014 configure GOOGLE_OAUTH_CLIENT_ID for live)");
        refreshAuth && refreshAuth();
      }
    } catch (e) {
      toast.error(e?.message || "Could not start Google sign-in");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Disable Google 2FA? You will no longer be required to verify via Google.")) return;
    setBusy(true);
    try {
      await api.post("/auth/google/disable");
      toast.success("Google 2FA disabled");
      refreshAuth && refreshAuth();
    } catch (e) { toast.error("Disable failed"); }
    finally { setBusy(false); }
  };

  const enabled = user?.mfa_enabled === true;

  return (
    <Card className="p-4 space-y-3" data-testid="google-2fa-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${enabled ? "bg-success/10 text-success border border-success/30" : "bg-muted/40 text-muted-foreground border border-border"}`}>
            {enabled ? <ShieldCheck className="h-5 w-5" /> : <ShieldOff className="h-5 w-5" />}
          </div>
          <div>
            <h3 className="text-sm font-semibold heading">Google Two-Factor Authentication</h3>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
              Add a second factor — verify with your Google account after entering your password. Stronger protection against compromised passwords.
            </p>
            <div className="mt-2 flex gap-2 flex-wrap">
              <Badge variant="outline" className={`text-[10px] ${enabled ? "text-success border-success/40" : "text-muted-foreground"}`}>
                {enabled ? "ENABLED" : "DISABLED"}
              </Badge>
              {cfg && (
                <Badge variant="outline" className={`text-[10px] ${cfg.enabled ? "text-accent border-accent/40" : "text-warning border-warning/40"}`}>
                  Provider: {cfg.mode}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="shrink-0">
          {enabled ? (
            <Button variant="outline" size="sm" onClick={disable} disabled={busy} data-testid="google-2fa-disable">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}Disable
            </Button>
          ) : (
            <Button size="sm" onClick={enable} disabled={busy} data-testid="google-2fa-enable">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}Enable Google 2FA
            </Button>
          )}
        </div>
      </div>
      {cfg && !cfg.enabled && (
        <div className="rounded-md border border-warning/30 bg-warning/5 p-2.5 text-xs flex items-start gap-2">
          <AlertCircle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />
          <div className="text-warning/90">
            Google OAuth is running in <b>MOCK</b> mode. To enable real Google sign-in, set <code className="bg-warning/10 px-1 rounded">GOOGLE_OAUTH_CLIENT_ID</code> in the backend environment (and optionally <code className="bg-warning/10 px-1 rounded">GOOGLE_OAUTH_ALLOWED_DOMAINS</code> for hosted-domain restriction).
          </div>
        </div>
      )}
    </Card>
  );
}
